import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { CustomerAddressDto, CustomerDto } from "./types";
import { renderClose } from "../../common/dialog";
import { customerDialogTitle, useCustomerBasicInfo } from "./common";
import { UseEntityStore, createEntityStore } from "../../common/entityStore";
import { useEffect, useState } from "react";
import { AddressInputProps, useAddressSuggestions } from "../../common/addressSuggestions";
import { useDefaultValues } from "../../common/defaultValues";
import { propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { CustomerSexTypeId, useCustomerSexTypes, useDeliveryMethods } from "../../common/enum";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { getCustomerAddressBaseValidators } from "./validators";
import { createAxios } from "../../common/axios";

export function CustomerAddressDialog(props: {
    close: () => void;
    customerAddress: CustomerAddressDto | null | undefined;
    useCustomerStore: UseEntityStore<CustomerDto>;
    disabled: boolean;
}) {
    const customer = useCustomerBasicInfo(props.useCustomerStore);
    const [useAddressStore] = useState(() => createEntityStore<CustomerAddressDto>({
        data: props.customerAddress ?? {
            isActive: true,
            customerSexTypeId: CustomerSexTypeId.Unknown,
            title: "",
            fname: "",
            lname: "",
            strt1: "",
            strt2: "",
            zipCode: "",
            city: "",
            coadr: "",
            mob: "",
            tel: "",
            email: "",
        } as CustomerAddressDto,
        id: props.customerAddress?.id ?? null,
        saveProps: ["customerSexTypeId", "title", "fname", "lname",
            "strt1", "strt2", "zipCode", "city", "coadr", "mob", "tel", "email"],
        fetchOnInit: false,
        path: "customers/customerAddresses",
        qs: { customerId: props.useCustomerStore.getState().serverData.id },
        onSaved() {
            props.useCustomerStore.getState().fetchServerData();
            props.close();
        }
    }));
    const addressStore = useAddressStore(p => ({
        entityId: p.entityId,
        setPropValidators: p.setPropValidators,
        isSaving: p.isSaving,
        isActive: p.clientData.isActive,
    }));
    const isActiveInDb = props.useCustomerStore(p => p.serverData.customerAddresses.find(p => p.id === addressStore.entityId)?.isActive);
    const isActive = addressStore.entityId == null ? addressStore.isActive : isActiveInDb;
    const address = useAddressSuggestions(useAddressStore);
    const def = useDefaultValues(p => p.clientData);
    const deliveryMethods = useDeliveryMethods();
    const addressInputProps: AddressInputProps = { disabled: props.disabled };
    useEffect(() => addressStore.setPropValidators(getCustomerAddressBaseValidators(def.landCode, false, deliveryMethods.items.find(d => d.isDefault && d.code === "DPD"))), [addressStore, deliveryMethods, def.landCode]);
    function importFromCustomerDetails() {
        const customer = props.useCustomerStore.getState().clientData;
        const addressStore = useAddressStore.getState();
        addressStore.setClientDataProp("customerSexTypeId", customer.customerSexTypeId);
        addressStore.setClientDataProp("title", customer.title);
        addressStore.setClientDataProp("fname", customer.fname);
        addressStore.setClientDataProp("lname", customer.lname);
        addressStore.setClientDataProp("strt1", customer.strt1);
        addressStore.setClientDataProp("strt2", customer.strt2);
        addressStore.setClientDataProp("zipCode", customer.zipCode);
        addressStore.setClientDataProp("city", customer.city);
        addressStore.setClientDataProp("coadr", customer.coadr);
        addressStore.setClientDataProp("mob", customer.mob);
        addressStore.setClientDataProp("tel", customer.tel);
        addressStore.setClientDataProp("email", customer.email);
    }
    const [isTogglingIsActive, setIsTogglingIsActive] = useState(false);
    const isFetchingCustomer = props.useCustomerStore(p => p.isFetching);
    async function toggleIsActive() {
        try {
            setIsTogglingIsActive(true);
            await createAxios().put(`customers/customerAddresses/${addressStore.entityId}/isActive`,
                { isActive: !isActive });
        }
        finally {
            setIsTogglingIsActive(false);
            props.useCustomerStore.getState().fetchServerData();
        }
    }
    return <>
        <DialogTitle>{customerDialogTitle("Delivery address", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={4}>{propNumberField(useAddressStore)("position", null, "Address ID", { disabled: true })}</Grid>
                <Grid item xs={4}>{propEnumField(useAddressStore)("customerSexTypeId", "Gender", useCustomerSexTypes, { disabled: props.disabled })}</Grid>
                <Grid item xs={4}></Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("title", "Title", { disabled: props.disabled })}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("fname", "First name", { disabled: props.disabled })}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("lname", "Last name", { disabled: props.disabled })}</Grid>

                <Grid item xs={12}>{propTextField(useAddressStore)("coadr", "C/O Address", { disabled: props.disabled })}</Grid>

                <Grid item xs={8}>{address.renderStreetInput(addressInputProps)}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("strt2", "House number", { disabled: props.disabled })}</Grid>

                <Grid item xs={4}>{address.renderZipInput(addressInputProps)}</Grid>
                <Grid item xs={8}>{address.renderCityInput(addressInputProps)}</Grid>

                <Grid item xs={4}>{propTextField(useAddressStore)("tel", "Phone", { disabled: props.disabled })}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("mob", "Mobile", { disabled: props.disabled })}</Grid>
                <Grid item xs={4} />

                <Grid item xs={12}>{propTextField(useAddressStore)("email", "E-mail", { disabled: props.disabled })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button color="edit" disabled={props.disabled || isTogglingIsActive || isFetchingCustomer || addressStore.entityId == null} onClick={toggleIsActive}>
                {isActive ? "Deactivate" : "Activate"}</Button>
            <Button onClick={importFromCustomerDetails} disabled={addressStore.isSaving || props.disabled}>Import from customer details</Button>
            <EntityStoreSaveButton useStore={useAddressStore} text={addressStore.entityId == null ? "Add" : "Update"} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}
